import { Box, Divider, Link, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useState } from "react";
import { getOccupationForCode } from "@kenetto/user-sdk";
import ContainerWrapper from "../../components/ContainerWrapper/ContainerWrapper";
import { useUserState } from "../../state/user";
import DocumentRow from "../../components/professional/DocumentRow/DocumentRow";
import UpdateSpecialtyDialog from "../../components/professional/UpdateSpecialtyDialog/UpdateSpecialtyDialog";
import UpdateContactsDialog from "../../components/professional/UpdateContactsDialog/UpdateContactsDialog";
import AddAddressDialog from "../../components/professional/AddAddressDialog/AddAddressDialog";
import ConfirmRemoveDialog from "../../components/professional/ConfirmRemoveDialog/ConfirmRemoveDialog";
import AddressEntry from "./AddressEntry";
import {
  IDENTIFYING_DOCUMENT_TYPE,
  currentDocument,
  isProfessionalOrder
} from "../../types/professional";
import { ProfessionalAddress } from "../../services/professionals";
import useProfessionalProfile from "../../hooks/useProfessionalProfile";
import { useTranslation } from "react-i18next";
import { typography } from "../../utils/typography";
import { semanticColors } from "../../utils/colors";

const useStyles = makeStyles(() => ({
  card: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    maxWidth: "600px",
    margin: "24px auto 0"
  },
  row: {
    padding: "16px 24px"
  },
  label: {
    ...typography.body.m,
    color: `${semanticColors.text.secondary} !important`,
    marginBottom: "4px !important"
  },
  value: {
    ...typography.body.lSemibold,
    color: `${semanticColors.text.primary} !important`
  },
  /** Names the kind of a grouped entry: the type of workplace, or which phone. */
  subLabel: {
    ...typography.body.lSemibold,
    color: `${semanticColors.text.secondary} !important`,
    marginTop: "8px !important"
  }
}));

/** Puts the update action beside the label it belongs to. */
const headerRowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2
};

/**
 * Formats a phone the way the professional's own screens show it.
 *
 * `countryPrefix` is an ISO country code, not a dial code. Only Portugal is
 * mapped: a number registered elsewhere is shown as it is rather than with a
 * bare "+" in front of it, which is what a missing mapping would produce.
 */
function formatPhone(phone?: { number?: string; countryPrefix?: string }) {
  if (!phone?.number) return null;
  return phone.countryPrefix === "PT" ? `+351 ${phone.number}` : phone.number;
}

export default function ProfessionalData() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { user } = useUserState();
  const { removeAddress } = useProfessionalProfile();

  /**
   * The API sends occupation and specialty as numeric codes and never their
   * names, so they are resolved through the same SDK the mobile app uses.
   */
  const occupation =
    typeof user?.occupation === "number"
      ? getOccupationForCode(user.occupation)
      : undefined;

  /**
   * Looked up by `code`, not by position. The mobile indexes the array
   * directly, which is off for three of the nursing specialties — code 6 lands
   * outside the array entirely.
   */
  const specialty = occupation?.specialties?.find(
    (item: any) => item?.code === user?.occupationSpecialty
  );

  const prescriptionLocale = [
    user?.prescriptionLocaleName,
    user?.prescriptionLocaleCode
  ]
    .filter(Boolean)
    .join(" - ");

  const addresses: any[] = user?.personalInfo?.addresses
    ? Object.values(user.personalInfo.addresses)
    : [];

  const cmdPhone = formatPhone(user?.cmdPhone);
  const professionalPhone = formatPhone(user?.professionalPhone);

  const [updatingSpecialty, setUpdatingSpecialty] = useState(false);
  const [updatingContacts, setUpdatingContacts] = useState(false);
  const [addingAddress, setAddingAddress] = useState(false);
  const [
    removingAddress,
    setRemovingAddress
  ] = useState<ProfessionalAddress | null>(null);

  // A professional always keeps one practice address: with only one left, its
  // remove action is disabled rather than left to come back as a 400.
  const lastPracticeAddress =
    addresses.filter(address => address?.professional).length <= 1;

  /**
   * The credential in force. Renewing files a new document rather than editing
   * the old one, so the most recently registered is the current one.
   */
  const credential = currentDocument(
    user?.personalInfo?.identifyingDocuments,
    isProfessionalOrder
  );

  const rows = [
    {
      label: t("professionalData.occupation"),
      value: occupation?.local ?? "-"
    },
    {
      label: t("professionalData.specialty"),
      value: specialty?.local ?? "-",
      onUpdate: () => setUpdatingSpecialty(true)
    },
    {
      label: t("professionalData.clinicalName"),
      value: user?.clinicalName ?? "-"
    },
    {
      label: t("professionalData.professionalOrder"),
      value: occupation?.association?.local ?? "-"
    },
    {
      label: t("professionalData.prescriptionLocale"),
      value: prescriptionLocale || "-"
    }
  ];

  /* Named rather than written inline, so the card below reads as the sequence
     of sections it is. */
  const workplacesSection = (
    <>
      {/* Addresses are add + remove only — the API has no update route — so
         changing a workplace means registering the new one and removing the
         old. */}
      <div className={classes.row}>
        <Box sx={headerRowSx}>
          <Typography className={classes.label}>
            {t("professionalData.workplace")}
          </Typography>
          <Link
            component="button"
            type="button"
            onClick={() => setAddingAddress(true)}
          >
            {t("contactLists.add")}
          </Link>
        </Box>
        {addresses.length === 0 ? (
          <Typography className={classes.value}>
            {t("contactLists.noAddresses")}
          </Typography>
        ) : (
          addresses.map((address, i) => (
            <AddressEntry
              key={address?._id ?? i}
              address={address}
              separated={i > 0}
              lastPracticeAddress={lastPracticeAddress}
              onRemove={() => setRemovingAddress(address)}
            />
          ))
        )}
      </div>
    </>
  );

  return (
    <ContainerWrapper>
      <Typography
        sx={{
          ...typography.heading.l,
          textAlign: "center",
          color: semanticColors.text.primary,
          mb: 0
        }}
      >
        {t("professionalData.title")}
      </Typography>

      <div className={classes.card}>
        {rows.map((row, i) => (
          <Box key={i}>
            <div className={classes.row}>
              <Box sx={headerRowSx}>
                <Typography className={classes.label}>{row.label}</Typography>
                {row.onUpdate && (
                  <Link component="button" type="button" onClick={row.onUpdate}>
                    {t("professionalData.update")}
                  </Link>
                )}
              </Box>
              <Typography className={classes.value}>{row.value}</Typography>
            </div>
            <Divider />
          </Box>
        ))}

        {/* The credential carries its own actions, so it is not a plain row.
            It shows even with nothing on file: the licence number is known from
            the profile, and this is the only way to file the card itself. */}
        <div className={classes.row}>
          <DocumentRow
            label={t("identifyingDocuments.professionalOrderNumber")}
            document={credential}
            professional
            type={Math.abs(IDENTIFYING_DOCUMENT_TYPE.PROFESSIONAL_ORDER)}
            fallbackNumber={user?.professionalOrderId}
            viewTitle={t("identifyingDocuments.professionalOrder")}
            updateTitle={t("identifyingDocuments.updateProfessionalOrder")}
            addTitle={t("identifyingDocuments.addProfessionalOrder")}
          />
        </div>
        <Divider />

        {workplacesSection}
        <Divider />

        <div className={classes.row}>
          <Box sx={headerRowSx}>
            <Typography className={classes.label}>
              {t("professionalData.contacts")}
            </Typography>
            <Link
              component="button"
              type="button"
              onClick={() => setUpdatingContacts(true)}
            >
              {t("professionalData.update")}
            </Link>
          </Box>
          {cmdPhone && (
            <>
              <Typography className={classes.subLabel}>
                {t("professionalData.cmdPhone")}
              </Typography>
              <Typography className={classes.value}>{cmdPhone}</Typography>
            </>
          )}
          {professionalPhone && (
            <>
              <Typography className={classes.subLabel}>
                {t("professionalData.professionalPhone")}
              </Typography>
              <Typography className={classes.value}>
                {professionalPhone}
              </Typography>
            </>
          )}
          {!cmdPhone && !professionalPhone && (
            <Typography className={classes.value}>-</Typography>
          )}
        </div>
      </div>

      {occupation && (
        <UpdateSpecialtyDialog
          open={updatingSpecialty}
          onClose={() => setUpdatingSpecialty(false)}
          specialties={occupation.specialties ?? []}
          currentCode={user?.occupationSpecialty}
          onUpdated={() => setUpdatingSpecialty(false)}
        />
      )}

      <AddAddressDialog
        open={addingAddress}
        onClose={() => setAddingAddress(false)}
        onAdded={() => setAddingAddress(false)}
      />

      <ConfirmRemoveDialog
        open={removingAddress !== null}
        title={t("contactLists.removeAddressTitle")}
        subject={[
          removingAddress?.name,
          removingAddress?.street,
          removingAddress?.city
        ]
          .filter(Boolean)
          .join(", ")}
        onClose={() => setRemovingAddress(null)}
        onConfirm={async () => {
          if (!removingAddress?._id) return;
          await removeAddress(removingAddress._id);
        }}
      />

      <UpdateContactsDialog
        open={updatingContacts}
        onClose={() => setUpdatingContacts(false)}
        cmdPhone={user?.cmdPhone}
        professionalPhone={user?.professionalPhone}
        onUpdated={() => setUpdatingContacts(false)}
      />
    </ContainerWrapper>
  );
}
